/**
 * ResultSummary
 * =============
 * Several summary views for one exam: overview stats, grade
 * distribution, class-wise performance and top performers.
 */

import React, { useMemo, useState } from 'react';
import { gradeForPercentage, fetchSchoolInfo, fetchClassGroups } from './resultsApi';
import { exportClassWiseExcel } from './classWiseExport';

const ResultSummary = ({ data, grades = [], schoolId }) => {
  const students = data?.students || [];
  const [exporting, setExporting] = useState(false);

  const handleClassWiseExcel = async () => {
    setExporting(true);
    try {
      let school = null;
      let groups = [];
      const [schoolRes, groupsRes] = await Promise.allSettled([
        fetchSchoolInfo(schoolId),
        fetchClassGroups(schoolId),
      ]);
      if (schoolRes.status === 'fulfilled') school = schoolRes.value;
      if (groupsRes.status === 'fulfilled') groups = groupsRes.value.groups || [];
      exportClassWiseExcel(data, grades, school, groups);
    } finally {
      setExporting(false);
    }
  };

  const stats = useMemo(() => {
    if (!students.length) return null;

    const percentages = students.map((s) => s.percentage || 0);
    const total = percentages.reduce((a, b) => a + b, 0);
    const avg = total / students.length;
    const highest = Math.max(...percentages);
    const lowest = Math.min(...percentages);

    // Grade distribution
    const gradeCount = {};
    grades.forEach((g) => { gradeCount[g.grade] = 0; });
    students.forEach((s) => {
      const g = gradeForPercentage(s.percentage, grades);
      gradeCount[g] = (gradeCount[g] || 0) + 1;
    });

    // Class-wise
    const classMap = {};
    students.forEach((s) => {
      const key = s.Class || '—';
      if (!classMap[key]) classMap[key] = { className: key, count: 0, sum: 0 };
      classMap[key].count += 1;
      classMap[key].sum += s.percentage || 0;
    });
    const classWise = Object.values(classMap)
      .map((c) => ({ ...c, avg: c.count ? c.sum / c.count : 0 }))
      .sort((a, b) => a.className.localeCompare(b.className));

    // Top performers
    const toppers = [...students]
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 5);

    return { avg, highest, lowest, gradeCount, classWise, toppers };
  }, [students, grades]);

  if (!stats) {
    return <div className="results-empty"><p>No results to summarise</p></div>;
  }

  const maxGradeCount = Math.max(1, ...Object.values(stats.gradeCount));

  return (
    <div className="results-summary">
      {/* Overview cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <span className="summary-card-value">{students.length}</span>
          <span className="summary-card-label">Students</span>
        </div>
        <div className="summary-card">
          <span className="summary-card-value">{stats.avg.toFixed(1)}%</span>
          <span className="summary-card-label">Average</span>
        </div>
        <div className="summary-card">
          <span className="summary-card-value summary-good">{stats.highest.toFixed(1)}%</span>
          <span className="summary-card-label">Highest</span>
        </div>
        <div className="summary-card">
          <span className="summary-card-value summary-bad">{stats.lowest.toFixed(1)}%</span>
          <span className="summary-card-label">Lowest</span>
        </div>
      </div>

      <div className="summary-grid">
        {/* Grade distribution */}
        <div className="summary-panel">
          <h3 className="summary-panel-title">Grade Distribution</h3>
          <div className="summary-bars">
            {grades.map((g) => {
              const count = stats.gradeCount[g.grade] || 0;
              return (
                <div key={g.grade} className="summary-bar-row">
                  <span className="summary-bar-label">{g.grade}</span>
                  <div className="summary-bar-track">
                    <div
                      className="summary-bar-fill"
                      style={{ width: `${(count / maxGradeCount) * 100}%` }}
                    />
                  </div>
                  <span className="summary-bar-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Class-wise */}
        <div className="summary-panel">
          <div className="summary-panel-head">
            <h3 className="summary-panel-title">Class-wise Average</h3>
            <button
              type="button"
              className="results-export-btn"
              onClick={handleClassWiseExcel}
              disabled={exporting || !students.length}
              title="One sheet per class & section: marks + grade per subject and grade distribution"
            >
              {exporting ? 'Exporting…' : '⬇ Class-wise Excel'}
            </button>
          </div>
          <table className="summary-table">
            <thead>
              <tr><th>Class</th><th>Students</th><th>Avg %</th></tr>
            </thead>
            <tbody>
              {stats.classWise.map((c) => (
                <tr key={c.className}>
                  <td>{c.className}</td>
                  <td>{c.count}</td>
                  <td>{c.avg.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top performers */}
        <div className="summary-panel">
          <h3 className="summary-panel-title">Top Performers</h3>
          <table className="summary-table">
            <thead>
              <tr><th>#</th><th>Student</th><th>Class</th><th>Sec</th><th>%</th><th>Grade</th></tr>
            </thead>
            <tbody>
              {stats.toppers.map((s, i) => (
                <tr key={s.Student_Id}>
                  <td>{i + 1}</td>
                  <td>{s.Student_Name || '—'}</td>
                  <td>{s.Class || '—'}</td>
                  <td>{s.Section || '—'}</td>
                  <td>{(s.percentage || 0).toFixed(1)}%</td>
                  <td>{gradeForPercentage(s.percentage, grades)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResultSummary;
